import { useCallback, useMemo } from 'react';

import { uniqBy } from 'lodash-es';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import {
	SEARCH_CACHE_SECONDS,
	SEARCH_LOCATION_CITY_MAX_GROUPS,
	SEARCH_LOCATION_SUGGESTIONS_MAX_ITEMS,
	TYPESENSE_LOCATION_COLLECTION,
	TYPESENSE_LOCATION_FILTER,
} from '#src/app/pages/search/shared/consts';

import { useSearchClient } from '../../../../../useSearchClient';
import type { RankedLocationItem } from '../../../../utils';
import { getMatchTier, normalizeLocationText } from '../../../../utils';

type LocationHit = {
	id: string;
	city: string;
	state_code: string;
	country_code: string;
};

type Result = ModelHookResult<
	undefined,
	{
		searchForCities: (queryLocation: string, selectedLocation: string) => Promise<readonly RankedLocationItem[]>;
	}
>;

export function useSearchForCities(): Result {
	const {
		data: { typesenseClient },
	} = useSearchClient();

	const {
		data: { countries },
	} = useSearchPageData();

	const countryDisplayNameMap = useMemo(
		() => Object.fromEntries(countries.map((c) => [c.countryCode, c.displayName])),
		[countries],
	);

	const getCityOrStateLocation = useCallback((city?: string, stateCode?: string) => {
		if (!city || !stateCode) return `${city || stateCode}`;
		return `${city}, ${stateCode}`;
	}, []);

	return {
		actions: {
			searchForCities: useCallback(
				async (queryLocation: string, selectedLocation: string) => {
					// the collection has one doc per site DB row (e.g. hundreds for "Los Angeles, CA"),
					// so we group by place and use the group size as a popularity signal
					const response = await typesenseClient
						.collections<LocationHit>(TYPESENSE_LOCATION_COLLECTION)
						.documents()
						.search(
							{
								q: queryLocation,
								query_by: TYPESENSE_LOCATION_FILTER,
								group_by: 'city,state_code,country_code',
								group_limit: 1,
								per_page: SEARCH_LOCATION_CITY_MAX_GROUPS,
							},
							{
								cacheSearchResultsForSeconds: SEARCH_CACHE_SECONDS,
							},
						);

					if (!Array.isArray(response.grouped_hits) || !response.grouped_hits.length) return [];

					const normalizedQuery = normalizeLocationText(queryLocation);

					const ranked = response.grouped_hits
						// group_limit: 1, so each group has a single hit
						.flatMap(({ hits, found }) =>
							hits.map(({ document }) => ({
								document,
								rowCount: found ?? 1,
								tier: getMatchTier(document.city || '', normalizedQuery),
							})),
						)
						.sort((a, b) => a.tier - b.tier || b.rowCount - a.rowCount);

					return uniqBy(
						ranked.map(({ document, tier }) => {
							const { city, state_code: stateCode, country_code: countryCode } = document;

							const cityStateValue = getCityOrStateLocation(city, stateCode);
							// without a state, "London" is ambiguous, so the label shows the country instead
							const countryName = countryCode ? countryDisplayNameMap[countryCode] : undefined;
							const label = !stateCode && city && countryName ? `${city}, ${countryName}` : cityStateValue;

							return {
								label,
								value: cityStateValue,
								count: 0,
								isRefined: selectedLocation === cityStateValue,
								type: 'city' as const,
								tier,
							};
						}),
						'value',
					).slice(0, SEARCH_LOCATION_SUGGESTIONS_MAX_ITEMS);
				},
				[countryDisplayNameMap, getCityOrStateLocation, typesenseClient],
			),
		},
	};
}
